import React, { useEffect } from "react"
import { Form, Radio, Input, Space } from "antd"
import { useTranslation } from "react-i18next"
import PaymentMethodList from "./PaymentMethodList"
import {
  paymentMethodsSelector,
  selectedPaymentMethodIdSelector,
  paymentMethodsLoadingSelector,
  getPaymentMethods,
  setSelectedPaymentMethodId
} from "store/user"
import { useSelector, useDispatch } from "react-redux"
import { useWatch } from "antd/es/form/Form"
import { getRefundDetails } from "store/bookings"
import styles from "./BookingForm.module.css"
import { validatePaymentAmount } from "../bookingUtils"
import PropTypes from "prop-types"
import { InfoCircleOutlined } from "@ant-design/icons"
import { numberToCurrency } from "helpers/string"
import { PAYMENT_OPTIONS } from "../constants"

const RefundPayment = ({ form, refundDueNow, isStep2, booking, bookingUuid, cash_incomes, card_incomes }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const paymentMethods = useSelector(paymentMethodsSelector)
  const paymentMethodsLoading = useSelector(paymentMethodsLoadingSelector)
  const hasPaymentMethods = !!paymentMethods.length
  const clientId = useSelector((state) => state.bookings.clientId)
  const selectedPaymentMethodId = useSelector(selectedPaymentMethodIdSelector)
  const refundBy = useWatch("refundBy", form)
  const paymentType = useWatch("paymentType", form)
  const collectPayment = useWatch("collectPayment", form)
  const recordPayment = useWatch("recordPayment", form)
  const { full_cash } = PAYMENT_OPTIONS

  let recordPaymentLabel = t("booking.labels.record_payment")
  if (refundBy === "cash" && booking?.payment_option === full_cash) {
    recordPaymentLabel = "Record Refund Amount"
  } else if (booking?.less_then_24h && refundBy !== "cash") {
    recordPaymentLabel = "Refund Amount"
  }

  const handleMethodChange = (e) => {
    form.setFieldsValue({
      refundBy: e.target.value
    })
    if (e.target.value !== "cash") {
      form.setFieldsValue({
        recordPayment: null
      })
      dispatch(setSelectedPaymentMethodId(booking?.last_selected_payment_method))
    }
  }
  const handlePaymentDetailsChange = (key, value) => {
    form.setFieldsValue({ [key]: value })
  }

  useEffect(() => {
    dispatch(getRefundDetails(bookingUuid))
  }, [bookingUuid, dispatch])

  useEffect(() => {
    if (paymentType !== "cash") {
      form.setFieldsValue({
        recordPayment: null
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType, form, refundBy])

  useEffect(() => {
    const currentValues = form.getFieldsValue(true)
    if (refundDueNow > 0) {
      form.setFieldsValue({
        ...currentValues,
        refundBy: currentValues.refundBy || "cash" // Only set to "cash" if not already set
      })
    }
  }, [form, refundDueNow])

  useEffect(() => {
    if (clientId) {
      dispatch(getPaymentMethods(clientId))
    }
  }, [clientId, dispatch])

  useEffect(() => {
    const shouldShowRefundInput =
      (refundBy === "cash" && booking?.payment_option === full_cash) || (booking?.less_then_24h && refundBy !== "cash")

    if (shouldShowRefundInput && refundDueNow > 0) {
      const currentRecordPayment = form.getFieldValue("recordPayment")
      // Auto-populate only if field is empty or undefined
      if (!currentRecordPayment) {
        form.setFieldsValue({
          recordPayment: refundDueNow
        })
      }
    }
  }, [refundDueNow, refundBy, booking?.payment_option, booking?.less_then_24h, full_cash, form])

  return (
    <div>
      {isStep2 && refundDueNow > 0 && (
        <>
          {!booking?.less_then_24h && booking?.payment_option === full_cash && (
            <Form.Item name="refundBy" className={styles.formItem}>
              <Radio.Group onChange={handleMethodChange} className={styles.radioGroup}>
                <Radio value="cash" disabled={cash_incomes?.length === 0}>
                  In Cash
                </Radio>
                <Radio value="card" disabled={card_incomes?.length === 0 || card_incomes === undefined}>
                  To Stripe
                </Radio>
              </Radio.Group>
            </Form.Item>
          )}
          {booking?.less_then_24h && (
            <Form.Item name="collectPayment" label={t("booking.labels.refund_payment_now")}>
              <Radio.Group value={collectPayment} onChange={(e) => handlePaymentDetailsChange("collectPayment", e.target.value)}>
                <Space direction="horizontal" wrap>
                  <Radio value="yes">{t("global.yes")}</Radio>
                  <Radio value="no">{t("global.no")}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          )}
          {collectPayment === "no" ? (
            <span>No refund will be issued to the client.</span>
          ) : (
            <>
              {((refundBy === "cash" && booking?.payment_option === full_cash) || (booking?.less_then_24h && refundBy !== "cash")) && (
                <div className={styles.cashPaymentContainer}>
                  <Form.Item
                    name="recordPayment"
                    label={recordPaymentLabel}
                    rules={[
                      {
                        validator: (_, value) =>
                          validatePaymentAmount(value, {
                            maxAmount: refundDueNow,
                            isRefund: true,
                            t
                          })
                      }
                    ]}
                  >
                    <Input placeholder={recordPaymentLabel} prefix="$" type="number" min="0.01" step="0.01" />
                  </Form.Item>
                </div>
              )}
              {booking?.less_then_24h && recordPayment > 0 && (
                <div className={styles.infoNotice}>
                  <InfoCircleOutlined className={styles.infoNoticeIcon} />
                  <span className={styles.infoNoticeText}>
                    {t("booking.refund_amount_info", { amount: numberToCurrency(recordPayment) })}
                  </span>
                </div>
              )}
              {(refundBy === "card" || (refundBy === "card" && !booking?.less_then_24h)) && (
                <div className={styles.paymentMethodsContainer}>
                  {!paymentMethodsLoading && hasPaymentMethods && (
                    <PaymentMethodList active={selectedPaymentMethodId} paymentMethods={paymentMethods} className="vstack gap-15" />
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

RefundPayment.propTypes = {
  form: PropTypes.object.isRequired,
  refundDueNow: PropTypes.number.isRequired,
  isStep2: PropTypes.bool.isRequired,
  booking: PropTypes.object.isRequired,
  bookingUuid: PropTypes.string.isRequired,
  cash_incomes: PropTypes.array,
  card_incomes: PropTypes.array
}

export default RefundPayment
